import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { ease, viewportOnce } from '../motion';

interface SectionHeadingProps {
  index: string; // e.g. "01"
  eyebrow: string; // e.g. "about"
  title: React.ReactNode;
  subtitle?: string;
  align?: 'left' | 'center';
}

/** Consistent section header: mono index + eyebrow, display title, optional subtitle. */
const SectionHeading: React.FC<SectionHeadingProps> = ({
  index,
  eyebrow,
  title,
  subtitle,
  align = 'left',
}) => (
  <Box sx={{ textAlign: align, mb: { xs: 6, md: 9 }, maxWidth: align === 'center' ? 720 : undefined, mx: align === 'center' ? 'auto' : undefined }}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ duration: 0.6, ease: ease.out }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2.5,
          justifyContent: align === 'center' ? 'center' : 'flex-start',
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: (t) => t.typography.button.fontFamily,
            color: 'primary.main',
            fontSize: '0.85rem',
          }}
        >
          {index}
        </Box>
        <Box sx={{ width: 28, height: '1px', backgroundColor: 'divider' }} />
        <Box
          component="span"
          sx={{
            fontFamily: (t) => t.typography.button.fontFamily,
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            fontSize: '0.72rem',
            color: 'text.secondary',
          }}
        >
          {eyebrow}
        </Box>
      </Box>
      <Typography variant="h2" component="h2" sx={{ color: 'text.primary' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            color: 'text.secondary',
            maxWidth: 620,
            mx: align === 'center' ? 'auto' : 0,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </motion.div>
  </Box>
);

export default SectionHeading;
